/** @jsxImportSource @emotion/react */
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { FormProvider, useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import "twin.macro";
import qs from "qs";
import Alert from "../shared/Alert";
import { PrimaryButton, PrimaryLinkButton } from "../shared/Button";
import { ErrorMessage, FormGroup, Input, Label, RequiredAsterisk } from "../shared/Form";
import { useSearchParams } from "../shared/useSearchParams";
import { useLoginMutation, useLoginMutationEnroll } from "./AuthAPI";
import { AuthLayoutDescription, AuthLayoutHeader, AuthLayoutTitle } from "./AuthLayout";

const LoginRoute = () => {
  const { t } = useTranslation();
  const formProps = useForm({
    defaultValues: { email: "", password: "" },
  });
  const location = useLocation();
  const searchParams = useSearchParams();
  // Enroll company if not already registered
  if (location.pathname === "/partnership") {
    localStorage.setItem("EnrollStudentSchoolUuid", searchParams.school);
    localStorage.setItem("EnrollStudentSchoolName", searchParams.schoolName);
  }

  if (location.pathname === "/confirmedAccount") {
    localStorage.setItem("EnrollStudentSchoolUuid", searchParams.school);
    localStorage.setItem("EnrollStudentSchoolName", searchParams.schoolname);
  }

  const enrollStudentSchoolUuid = localStorage.getItem("EnrollStudentSchoolUuid");
  const { mutate: login, status, error } = useLoginMutation();
  const { mutate: loginEnroll, statusEnroll, errorEnroll } = useLoginMutationEnroll();
  // EO Enroll company if not already registered

  const { handleSubmit } = formProps;

  const [confirmedAccount] = useState(
    location && location.pathname && location.pathname === "/confirmedAccount"
  );
  return (
    <>
      <Helmet title={t("auth.sign-in-to-your-account")} />
      <AuthLayoutHeader schoolName={searchParams.schoolName} school={searchParams.school}>
        <AuthLayoutTitle>{t("auth.sign-in-to-your-account")}</AuthLayoutTitle>
        <AuthLayoutDescription>
          <Trans i18nKey="auth.or-register-a-new-account">
            Or{" "}
            <PrimaryLinkButton
              as={Link}
              to={`/register${qs.stringify({ ...searchParams }, { addQueryPrefix: true })}`}
            >
              register a new account
            </PrimaryLinkButton>
          </Trans>
        </AuthLayoutDescription>
      </AuthLayoutHeader>
      {confirmedAccount && (
        <Alert
          tw="mt-8"
          variant="info"
          title={t("auth.account-confirmed-title")}
          description={t("auth.account-confirmed-description")}
        />
      )}
      <FormProvider {...formProps}>
        {enrollStudentSchoolUuid ? (
          <form onSubmit={handleSubmit(loginEnroll)} tw="space-y-6 mt-8">
            {errorEnroll && <Alert title={t(errorEnroll.message)} />}

            <FormGroup>
              <Label>
                {t("auth.email-address")} <RequiredAsterisk />
              </Label>
              <Input type="email" name="email" autoComplete="username" rules={{ required: true }} />
              <ErrorMessage name="email" />
            </FormGroup>

            <FormGroup>
              <Label>
                {t("auth.password")} <RequiredAsterisk />
              </Label>
              <Input
                type="password"
                name="password"
                autoComplete="current-password"
                rules={{ required: true }}
              />
              <ErrorMessage name="password" />
            </FormGroup>

            <div tw="flex items-center justify-end">
              <PrimaryLinkButton as={Link} to="/forgot-password">
                {t("auth.forgot-your-password")}
              </PrimaryLinkButton>
            </div>

            <PrimaryButton type="submit" tw="w-full" disabled={statusEnroll === "loading"}>
              {t("auth.sign-in")}
            </PrimaryButton>
          </form>
        ) : (
          <form onSubmit={handleSubmit(login)} tw="space-y-6 mt-8">
            {error && <Alert title={t(error.message)} />}

            <FormGroup>
              <Label>
                {t("auth.email-address")} <RequiredAsterisk />
              </Label>
              <Input type="email" name="email" autoComplete="username" rules={{ required: true }} />
              <ErrorMessage name="email" />
            </FormGroup>

            <FormGroup>
              <Label>
                {t("auth.password")} <RequiredAsterisk />
              </Label>
              <Input
                type="password"
                name="password"
                autoComplete="current-password"
                rules={{ required: true }}
              />
              <ErrorMessage name="password" />
            </FormGroup>

            <div tw="flex items-center justify-end">
              <PrimaryLinkButton as={Link} to="/forgot-password">
                {t("auth.forgot-your-password")}
              </PrimaryLinkButton>
            </div>

            <PrimaryButton type="submit" tw="w-full" disabled={status === "loading"}>
              {t("auth.sign-in")}
            </PrimaryButton>
          </form>
        )}
      </FormProvider>
    </>
  );
};

export default LoginRoute;
